import { Switch } from "@/components/Switch";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import { useGetOrganizationPolicies, useUpdateOrganizationPolicies } from "@/queries/organization-queries";
import { formatError } from "@/utils/errors";
import type { PartialMessage } from "@bufbuild/protobuf";
import type { OrganizationPolicies } from "gitpod-next-api/gitpod/v1/organization_pb";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/flexkit/Button";
import { IconSettings } from "@/assets/icons/geist/IconSettings";
import { useNavigate } from "react-router-dom";
import { RunnerUsageButtons } from "@/components/runners/RunnerUsageButtons";

type GitpodDesktopProps = {
    editable?: boolean;
};

export const GitpodDesktop: FC<GitpodDesktopProps> = ({ editable = true }) => {
    const navigate = useNavigate();
    const { data: organizationPolicies, isLoading } = useGetOrganizationPolicies();
    const [enabled, setEnabled] = useState(false);
    const updateOrganizationPolicies = useUpdateOrganizationPolicies();
    const { toast } = useToast();

    useEffect(() => {
        if (organizationPolicies) {
            setEnabled(!!organizationPolicies.allowLocalRunners);
        }
    }, [organizationPolicies]);

    const handleToggle = useCallback(
        async (newValue: boolean) => {
            const params: PartialMessage<OrganizationPolicies> = {
                allowLocalRunners: newValue,
            };

            try {
                await updateOrganizationPolicies.mutateAsync(params);
                setEnabled(newValue);
                toast({
                    title: newValue ? "Ona Desktop enabled" : "Ona Desktop disabled",
                });
            } catch (error) {
                console.error(error);
                toast({
                    title: "We couldn't save your changes. Sorry about that! Please retry.",
                    description: formatError(error),
                });
            }
        },
        [updateOrganizationPolicies, toast],
    );

    return (
        <SkeletonBlock ready={!isLoading}>
            <div className="rounded-xl border border-border-base">
                <div className="flex flex-col justify-between gap-4 px-5 py-4 lg:flex-row lg:items-center lg:gap-8">
                    <div className="flex min-w-0 items-center gap-4">
                        <Switch
                            id="gitpod-desktop-switch"
                            state={enabled ? "checked" : "unchecked"}
                            onToggle={handleToggle}
                            disabled={!editable}
                            aria-label="Allow local environments using Ona Desktop"
                        />
                        <div className="flex min-w-0 flex-col gap-1">
                            <Text className="text-base font-semibold">Allow local environments using Ona Desktop</Text>
                            <Text className="text-sm text-content-secondary">
                                This will affect all members using Ona Desktop on this organization
                            </Text>
                        </div>
                    </div>
                </div>
                <div className="flex flex-col justify-between gap-4 border-t border-border-base px-4 py-3 lg:flex-row lg:items-center lg:gap-8">
                    <RunnerUsageButtons
                        localRunner={true}
                        testIdPrefix="local-runner-settings"
                        className="flex flex-wrap items-center gap-4"
                    />
                    <Button
                        variant="secondary"
                        onClick={() => navigate("/settings/runners")}
                        data-testid="repository-access-settings-button"
                        className="flex items-center gap-2 self-start lg:self-auto"
                        data-tracking-id="repository-access-settings-gitpod-desktop"
                    >
                        <IconSettings size="base" />
                        Repository Access Settings
                    </Button>
                </div>
            </div>
        </SkeletonBlock>
    );
};
